<template>
  <div
    v-if="templatesType === 'none'"
  >
    <Dialog
      id="dialog-type-template"
      v-model:visible="displayDialog"
      :modal="true"
      :draggable="false"
      :closable="true"
      :breakpoints="{ '960px': '65vw', '640px': '90vw' }"
      :style="{ width: '55vw', maxWidth: '900px' }"
      @update:visible="handleDialogVisibilityChange"
    >
      <template #header>
        <div class="modal-header-custom">
          <div class="header-icon">
            <i class="fa-light fa-palette" />
          </div>
          <div class="header-content">
            <h3 class="header-title">
              {{ t('templateSelector.dialogTypeTemplate.header') }}
            </h3>
            <p class="header-subtitle">
              {{ t('templateSelector.dialogTypeTemplate.subtitle') }}
            </p>
          </div>
        </div>
      </template>

      <div class="template-type-grid">
        <div
          v-for="typeConfig in listTypesConfig"
          :key="typeConfig.type"
          class="template-type-card"
          :class="{ 'template-type-card--disabled': getTemplateTypeFlag(typeConfig.type)?.enabled === false }"
          @click="getTemplateTypeFlag(typeConfig.type)?.enabled === false ? null : chooseTemplatesType(typeConfig.type)"
        >
          <div class="template-type-card__icon">
            <i :class="typeConfig.icon" />
          </div>
          <div class="template-type-card__info">
            <span class="template-type-card__label">{{ typeConfig.label }}</span>
            <span class="template-type-card__desc">{{ typeConfig.description }}</span>
          </div>
          <ComingSoonBadge
            v-if="getTemplateTypeFlag(typeConfig.type)?.comingSoon"
            variant="floating"
          />
        </div>
      </div>
    </Dialog>
  </div>
  <div
    v-else
  >
    <EditorModal
      :title="t('templateSelector.title')"
      :show-close-button="true"
      @on-close-modal="closeModal"
    >
      <template #content>
        <div class="template-selector-layout">
          <!-- Left Sidebar Filters -->
          <div class="filters-sidebar">
            <div class="filters-sidebar__content">
              <!-- Search -->
              <div class="filter-section">
                <div class="search-wrapper">
                  <i class="fa fa-search search-icon" />
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="search-input"
                    :placeholder="t('templateSelector.search.placeholder')"
                  >
                  <div
                    v-if="searchQuery"
                    class="clear-search-btn"
                    @click="searchQuery = ''"
                  >
                    <i class="fa fa-times" />
                  </div>
                </div>
              </div>

              <!-- Category Filter -->
              <div class="filter-section">
                <label class="filter-section__label">
                  <i class="fa-light fa-folder" />
                  {{ t('templateSelector.categories.title') }}
                </label>
                <div class="filter-options">
                  <div
                    v-for="option in templateCategoriesOptions"
                    :key="option.category"
                    class="filter-option"
                    :class="{ 'filter-option--active': templateCategorySelected.category === option.category }"
                    @click="templateCategorySelected = option"
                  >
                    <i :class="option.icon" />
                    <span>{{ option.title }}</span>
                  </div>
                </div>
              </div>

              <!-- Author Filter (only for gallery) -->
              <div
                v-if="templateCategorySelected.category === 'gallery'"
                class="filter-section filter-section--authors"
              >
                <label class="filter-section__label">
                  <i class="fa-light fa-user" />
                  {{ t('templateSelector.filters.author') }}
                </label>

                <!-- Inline search once the list is long. -->
                <div
                  v-if="authorFilterOptions.length > 6"
                  class="author-list__search"
                >
                  <i class="fa-light fa-search" />
                  <input
                    v-model="authorSearchQuery"
                    type="text"
                    :placeholder="t('automatedScenarios.workflowSelector.filters.searchAuthor')"
                  >
                </div>

                <!-- Unified author list: ShopiMind + "me" + integrations,
                     all at the same level. Integrations carry a count. -->
                <div class="author-list">
                  <div
                    v-for="option in filteredAuthorOptions"
                    :key="option.value"
                    class="author-list__item"
                    :class="{
                      'author-list__item--integration': option.value !== 'shopimind' && option.value !== 'me',
                      'author-list__item--active': selectedAuthors.includes(option.value),
                    }"
                    @click="toggleAuthor(option.value)"
                  >
                    <span class="author-list__avatar">
                      <img
                        v-if="getAuthorIcon(option.value)"
                        :src="getAuthorIcon(option.value)"
                        :alt="option.label"
                      >
                      <i
                        v-else
                        :class="(option.value === 'shopimind' || option.value === 'me') ? 'fa-light fa-user' : 'fa-solid fa-plug'"
                      />
                    </span>
                    <span class="author-list__label">{{ option.label }}</span>
                    <span
                      v-if="option.count !== undefined"
                      class="author-list__count"
                    >{{ option.count }}</span>
                    <span
                      v-if="selectedAuthors.includes(option.value)"
                      class="author-list__check"
                    >
                      <i class="fa-solid fa-check" />
                    </span>
                  </div>
                  <div
                    v-if="filteredAuthorOptions.length === 0"
                    class="author-list__empty"
                  >
                    {{ t('automatedScenarios.workflowSelector.filters.noAuthorMatch') }}
                  </div>
                </div>
              </div>

              <!-- Scenario Filters (only for recent) -->
              <template v-if="templateCategorySelected.category === 'recent'">
                <div class="filter-section">
                  <label class="filter-section__label">
                    <i class="far fa-rotate" />
                    {{ t('templateSelector.filters.groupAutomation') }}
                  </label>
                  <Dropdown
                    v-model="selectedAutomation"
                    :options="automationOptions"
                    option-label="name"
                    :placeholder="t('templateSelector.filters.selectScenario')"
                    class="scenario-dropdown"
                    :filter="true"
                    :show-clear="true"
                    :loading="automationLoading"
                    @filter="(e) => onCampaignFilter(e, 'automation')"
                    @show="onAutomationShow"
                  />
                </div>
                <div class="filter-section">
                  <label class="filter-section__label">
                    <i class="far fa-paper-plane" />
                    {{ t('templateSelector.filters.groupBulk') }}
                  </label>
                  <Dropdown
                    v-model="selectedBulk"
                    :options="bulkOptions"
                    option-label="name"
                    :placeholder="t('templateSelector.filters.selectCampaign')"
                    class="scenario-dropdown"
                    :filter="true"
                    :show-clear="true"
                    :loading="bulkLoading"
                    @filter="(e) => onCampaignFilter(e, 'bulk')"
                    @show="onBulkShow"
                  />
                </div>
              </template>

              <!-- Results count -->
              <div class="filter-results">
                <span class="filter-results__count">{{ totalCount }}</span>
                <span class="filter-results__label">{{ t('templateSelector.filters.results') }}</span>
              </div>
            </div>
          </div>

          <!-- Templates Grid -->
          <main class="templates-content">
            <!-- Drill-in banner — shown when filtered to a single integration (G10). -->
            <DrilldownBanner
              v-if="drilledInIntegration"
              :name="drilledInIntegration.name"
              :icon="drilledInIntegration.icon"
              @back="clearAuthorFilter"
            />

            <Loader v-if="isListLoading" />

            <div
              v-else-if="filteredList.length"
              class="templates-grid"
            >
              <template
                v-for="record of displayItems"
                :key="record.__collection ? ('coll-' + record.slug) : (isGalleryItem(record) ? record.path : record.id_template)"
              >
                <IntegrationCollectionCard
                  v-if="record.__collection"
                  :name="record.name"
                  :icon="record.icon"
                  :count="record.count"
                  :status="record.status"
                  :previews="record.previews"
                  :kicker="t('automatedScenarios.workflowSelector.byPartner')"
                  :count-label="t('integrations.collection.count', { count: record.count })"
                  :open-label="t('integrations.collection.open', { count: record.count })"
                  :install-label="t('integrations.collection.install')"
                  @open="drillIntoIntegration(record.slug)"
                />
                <div
                  v-else
                  class="template-card"
                  :class="{
                    'template-card--integration':
                      isGalleryItem(record) && record.author === 'integration',
                  }"
                >
                  <div class="template-card__content">
                  <!-- Partner ribbon — only for integration-published
                       gallery items. Overhangs the top-left corner so it
                       stays visible above the hover overlay. -->
                  <div
                    v-if="isGalleryItem(record) && record.author === 'integration' && record.integration_name && !drilledInIntegration"
                    class="template-card__partner-ribbon"
                  >
                    <div class="template-card__partner-logo">
                      <img
                        v-if="record.integration_icon_url"
                        :src="record.integration_icon_url"
                        :alt="record.integration_name"
                      >
                      <i
                        v-else
                        class="fa-solid fa-plug"
                      />
                    </div>
                    <div class="template-card__partner-text">
                      <span class="template-card__partner-kicker">
                        {{ t('automatedScenarios.workflowSelector.byPartner') }}
                      </span>
                      <span class="template-card__partner-name">{{ record.integration_name }}</span>
                    </div>
                  </div>

                  <!-- Gallery Item: Image Preview from base64 -->
                  <div
                    v-if="isGalleryItem(record)"
                    class="template-card__image"
                  >
                    <div
                      v-if="record.previews && record.previews.length > 0"
                      class="template-card__preview"
                      :style="{ backgroundImage: `url(${record.previews[0]})` }"
                    />
                    <div
                      v-else
                      class="template-card__preview template-card__preview--placeholder"
                    >
                      <i class="fa-light fa-image" />
                    </div>
                    <!-- HTML preview icon for gallery items -->
                    <Button
                      v-if="record.htmlPreviewCount && record.htmlPreviewCount > 0"
                      class="template-card__html-preview-btn"
                      :title="t('templateSelector.buttons.htmlPreview')"
                      @click.stop="openGalleryHtmlPreview(record)"
                    >
                      <i class="fa-light fa-eye" />
                    </Button>
                  </div>

                  <!-- Regular Template: Image Preview from media URL -->
                  <div
                    v-else
                    class="template-card__image"
                  >
                    <img
                      v-show="false"
                      :src="`${baseUrl}${record.id_shop}_${getFirstImageKey(record.imageKey)}${
                        isDisplayTemplate(templatesType) ? '-isdisplay' : ''
                      }.png&w=600&${new Date().getTime()}`"
                      @load="imageLoaded(record.id_template)"
                      @error="imageFailed(record.id_template)"
                    >
                    <div
                      v-if="imageLoadTime[record.id_template] && imageLoadTime[record.id_template] > 0"
                      class="template-card__preview"
                      :style="{
                        backgroundImage: `url(${baseUrl}${record.id_shop}_${getFirstImageKey(record.imageKey)}${
                          isDisplayTemplate(templatesType) ? '-isdisplay' : ''
                        }.png&w=600&${imageLoadTime[record.id_template]})`
                      }"
                    />
                    <div
                      v-else
                      class="template-card__preview template-card__preview--loading"
                    >
                      <ProgressSpinner
                        style="width: 25px; height: 25px"
                        stroke-width="8"
                        fill="var(--surface-ground)"
                        animation-duration="1.0s"
                        aria-label="Custom ProgressSpinner"
                      />
                    </div>
                    <!-- HTML preview icon -->
                    <Button
                      v-if="imageLoadTime[record.id_template] && imageLoadTime[record.id_template] > 0"
                      class="template-card__html-preview-btn"
                      :title="t('templateSelector.buttons.htmlPreview')"
                      @click.stop="openHtmlPreview(record.id_shop, record.id_template)"
                    >
                      <i class="fa-light fa-eye" />
                    </Button>
                  </div>

                  <!-- Content -->
                  <div class="template-card__body">
                    <h3 class="template-card__title">
                      {{ isGalleryItem(record) ? getLocalizedText(record.title, 'Sans titre') : record.label }}
                    </h3>
                    <div
                      v-if="!isGalleryItem(record) && record.campaign_name"
                      class="template-card__scenario"
                    >
                      <i class="far fa-share-nodes" />
                      <span>{{ record.campaign_name }}</span>
                    </div>
                    <p
                      v-if="isGalleryItem(record) && record.description"
                      class="template-card__description"
                    >
                      {{ getLocalizedText(record.description) }}
                    </p>
                    <SpmBadge
                      v-if="!isGalleryItem(record) && record.whatsapp_status"
                      :label="getWhatsappStatusLabel(record.whatsapp_status)"
                      :icon="getWhatsappStatusIcon(record.whatsapp_status)"
                      :colors="getWhatsappStatusColors(record.whatsapp_status)"
                      size="sm"
                    />
                    <div
                      v-if="templateCategorySelected.category === 'gallery'"
                      class="template-card__author"
                    >
                      <i class="fa-light fa-user" />
                      <span>{{ getAuthorName(record) }}</span>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="template-card__actions">
                    <Button
                      :label="t('templateSelector.buttons.choose')"
                      class="p-button-sm p-button-success choose-btn"
                      @click="isGalleryItem(record) ? handleGalleryChoose(record) : handleChoose(record.id_shop, record.id_template, record.label)"
                    />
                  </div>
                </div>
              </div>
              </template>
            </div>

            <!-- Sentinel for infinite scroll + loading spinner -->
            <div
              v-if="filteredList.length && filteredList.length < totalCount"
              ref="scrollSentinelRef"
              class="load-more-spinner"
            >
              <ProgressSpinner
                v-if="isLoadingMore"
                style="width: 30px; height: 30px"
                stroke-width="6"
                fill="var(--surface-ground)"
                animation-duration="1.0s"
              />
            </div>

            <div
              v-if="!filteredList.length && !isListLoading"
              class="empty-state"
            >
              <i class="fa-light fa-inbox" />
              <p>{{ t('templateSelector.list.empty') }}</p>
            </div>
          </main>
        </div>
      </template>
    </EditorModal>

    <!-- HTML Preview Slider (reused from import) -->
    <TemplateHtmlPreviewSlider
      v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
      :previews="htmlPreviewData.previews"
      @close="closeHtmlPreview"
    />

    <PreviewModal
      v-model:visible="displayPreviewModal"
      :images="images"
      @on-close-preview-modal="handleClosePreview"
    />

    <RequireIntegrationDialog
      v-model:visible="requireIntegrationVisible"
      :integration="requireIntegrationData"
      :status-label="requireIntegrationStatus"
    />
  </div>
</template>

<script lang="ts">
import Button from 'primevue/button';
import {
  computed,
  defineComponent, onMounted, onBeforeUnmount, nextTick, Ref, ref, SetupContext, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import EditorModal from '@/components/modals/EditorModal.vue';
// SelectButton removed - using custom filter options
import Loader from '@/components/layout/Loader.vue';
import PreviewModal from '@/components/modals/PreviewModal.vue';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { UserState } from '@/composables/User';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { fetchUserTemplates, GalleryTemplateItem } from '@/composables/template/TemplateHelper';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import { useTemplatesApi } from '@/composables/templates/useTemplatesApi';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { store } from '@/store';
import { getTemplateParentTypeByType, isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import { generateNextStepName, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { getCountOperatorsByType, mappingTemplateTypeToOperatorType } from '@/composables/AutomatedScenarios';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import { nestCall, NestApiType } from '@/composables/nestApi';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { getFeatureFlagForTemplateType } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface TemplateWithShop extends TemplatesCustom {
  id_shop: number;
  createdBy?: string;
  whatsapp_status?: string;
}

interface BaseLinkData {
  namePrimaryTemplate?: string;
  countRedirectTemplate?: number;
  originTemplateId?: number | null;
  selectedTemplate?: any;
}

export default defineComponent({
  name: 'TemplateSelectorV2',

  components: {
    PreviewModal,
    Loader,
    EditorModal,
    Button,
    Dialog,
    ProgressSpinner,
    MultiSelect,
    Dropdown,
    SpmBadge,
    TemplateHtmlPreviewSlider,
    ComingSoonBadge,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  props: {
    typeTemplates: {
      type: String,
      required: true,
      default: '',
    },

    originCall: {
      type: String,
      required: false,
      default: '',
    },

    idCampaign: {
      type: [Number, null] as any,
      required: false,
      default: null,
    },

    duplicate: {
      type: Boolean,
      required: false,
      default: false,
    },

    baseLinkData: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    customTemplateType: {
      type: String,
      required: false,
      default: '',
    },

    forceDuplicate: {
      type: Boolean,
      required: false,
      default: false,
    },

    currentTemplateName: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-template': Object,
  },

  setup(props: {
    typeTemplates: string;
    originCall: string|null;
    idCampaign: number|null;
    duplicate: boolean;
    baseLinkData: BaseLinkData;
    customTemplateType: string;
    forceDuplicate: boolean;
    currentTemplateName: string;
  }, context: SetupContext) {
    const { t, locale } = useI18n();
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const baseUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    console.log('baseUrl master', baseUrl);

    const imageLoadTime = ref<Record<number, number>>({});
    const imagesLoaded = ref<Record<number, boolean>>({});
    const isListLoading = ref(false);
    const list: Ref<(TemplateWithShop | GalleryTemplateItem)[]> = ref([]);
    const templatesType = ref(props.typeTemplates);
    const displayPreviewModal = ref(false);
    const images = ref([{}]);
    const searchQuery = ref('');
    const limit = 12;
    const displayDialog = ref(false);
    const totalCount = ref(0);
    const isLoadingMore = ref(false);

    // Template types configuration with icons (style like MarketingPressure.vue)
    const listTypesConfig = ref([
      {
        type: TemplateTypeEnum.EMAIL,
        label: t('templateBuilder.fields.email'),
        description: t('templateSelector.dialogTypeTemplate.types.email.description'),
        icon: 'fa-light fa-envelope',
      },
      {
        type: TemplateTypeEnum.SMS,
        label: t('templateSelector.dialogTypeTemplate.types.sms.label'),
        description: t('templateSelector.dialogTypeTemplate.types.sms.description'),
        icon: 'fa-light fa-comment-sms',
      },
      {
        type: TemplateTypeEnum.PUSHNOTIFICATIONS,
        label: t('templateSelector.dialogTypeTemplate.types.pushnotifications.label'),
        description: t('templateSelector.dialogTypeTemplate.types.pushnotifications.description'),
        icon: 'fa-light fa-bell',
      },
      {
        type: TemplateTypeEnum.POPUP,
        label: t('templateSelector.dialogTypeTemplate.types.popup.label'),
        description: t('templateSelector.dialogTypeTemplate.types.popup.description'),
        icon: 'fa-light fa-window-restore',
      },
      {
        type: TemplateTypeEnum.EMBED,
        label: t('templateSelector.dialogTypeTemplate.types.embed.label'),
        description: t('templateSelector.dialogTypeTemplate.types.embed.description'),
        icon: 'fa-light fa-code',
      },
      {
        type: TemplateTypeEnum.RICHTEXTING,
        label: t('templateSelector.dialogTypeTemplate.types.richtexting.label'),
        description: t('templateSelector.dialogTypeTemplate.types.richtexting.description'),
        icon: 'fa-light fa-message-lines',
      },
      {
        type: TemplateTypeEnum.WHATSAPP,
        label: t('templateSelector.dialogTypeTemplate.types.whatsapp.label'),
        description: t('templateSelector.dialogTypeTemplate.types.whatsapp.description'),
        icon: 'fa-brands fa-whatsapp',
      },
    ]);

    const templateCategorySelected = ref({ category: 'recent', title: t('templateSelector.categories.recent'), icon: 'fa-light fa-clock' });
    const templateCategoriesOptions = ref([
      { category: 'recent', title: t('templateSelector.categories.recent'), icon: 'fa-light fa-clock' },
      { category: 'gallery', title: t('templateSelector.categories.gallery'), icon: 'fa-light fa-images' },
      { category: 'self', title: t('templateSelector.categories.self'), icon: 'fa-light fa-user' },
    ]);
    const previousCategory = ref('recent');

    // Author filter (MultiSelect - only for gallery).
    // Shared gating composable + `me` extra option for user-owned templates.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    // The "me" row is template-specific (other pickers only deal with
    // gallery items). Non-gallery items in `list` are user templates so
    // we don't pass them through the integration filter.
    const authorFilterOptions = authorFilterOptionsFor(
      () => list.value.filter(isGalleryItem),
      () => [{ value: 'me', label: t('templateSelector.filters.authorMe') }],
    );

    // ----- Visual author filter split (same UX as the other pickers) -----
    // ShopiMind row + "me" row sit on top (non-integration). All
    // integration partners are grouped below in their own scrollable
    // subsection with an inline search past 5 entries.

    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + "me" + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind' || slug === 'me') return undefined;
      const match = list.value.find(
        (item) => isGalleryItem(item) && item.integration_slug === slug,
      );
      if (match && isGalleryItem(match)) {
        return match.integration_icon_url ?? undefined;
      }
      return undefined;
    };

    // Scenario filters (two separate Dropdowns - only for recent)
    const selectedCampaign = ref<{ id: number; name: string; campaignType: string } | null>(null);

    // Automation dropdown state
    const selectedAutomation = ref<{ id: number; name: string; campaignType: string } | null>(null);
    const automationOptions = ref<{ id: number; name: string; campaignType: string }[]>([]);
    const automationLoading = ref(false);

    // Bulk dropdown state
    const selectedBulk = ref<{ id: number; name: string; campaignType: string } | null>(null);
    const bulkOptions = ref<{ id: number; name: string; campaignType: string }[]>([]);
    const bulkLoading = ref(false);

    let campaignFilterDebounce: ReturnType<typeof setTimeout> | null = null;

    const fetchCampaignsByType = async (
      campaignType: 'automation' | 'bulk',
      search = '',
    ) => {
      const optionsRef = campaignType === 'automation' ? automationOptions : bulkOptions;
      const loadingRef = campaignType === 'automation' ? automationLoading : bulkLoading;
      loadingRef.value = true;
      try {
        const queryParams = new URLSearchParams({
          type: templatesType.value,
          limit: '50',
          offset: '0',
          campaignType,
        });
        if (search) {
          queryParams.set('search', search);
        }
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/templates/selector/campaigns',
          {},
          `?${queryParams.toString()}`,
        );
        if (response?.success && Array.isArray(response.data)) {
          optionsRef.value = response.data;
        }
      } catch {
        // silent
      } finally {
        loadingRef.value = false;
      }
    };

    const onAutomationShow = () => {
      if (automationOptions.value.length === 0) {
        fetchCampaignsByType('automation');
      }
    };

    const onBulkShow = () => {
      if (bulkOptions.value.length === 0) {
        fetchCampaignsByType('bulk');
      }
    };

    const onCampaignFilter = (event: any, campaignType: 'automation' | 'bulk') => {
      if (campaignFilterDebounce) clearTimeout(campaignFilterDebounce);
      campaignFilterDebounce = setTimeout(() => {
        fetchCampaignsByType(campaignType, event.value);
      }, 300);
    };

    // When one dropdown is selected, clear the other
    watch(selectedAutomation, (val) => {
      if (val) {
        selectedBulk.value = null;
        selectedCampaign.value = val;
      } else if (!selectedBulk.value) {
        selectedCampaign.value = null;
      }
    });

    watch(selectedBulk, (val) => {
      if (val) {
        selectedAutomation.value = null;
        selectedCampaign.value = val;
      } else if (!selectedAutomation.value) {
        selectedCampaign.value = null;
      }
    });

    // Helper to check if a record is a gallery item
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isGalleryItem = (record: any): record is GalleryTemplateItem => record && record.isGalleryItem === true;

    // Current locale for i18n titles
    const currentLocale = computed(() => locale.value || 'fr');

    // Get localized text from a multilingual object (central util — G12)
    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    // Get display label for any record type
    const getRecordLabel = (record: TemplateWithShop | GalleryTemplateItem): string => {
      if (isGalleryItem(record)) {
        return getLocalizedText(record.title, 'Sans titre');
      }
      return record.label || '';
    };

    // Get author name from template
    const getAuthorName = (record: TemplateWithShop | GalleryTemplateItem): string => {
      if (isGalleryItem(record)) {
        if (record.author === 'integration') {
          return record.integration_name || record.integration_slug || 'Integration';
        }
        return 'Shopimind';
      }
      if (record.id_shop === 0) {
        return 'ShopiMind';
      }
      return record.createdBy || t('templateSelector.filters.authorMe');
    };

    // Filtered list based on author filter
    const filteredList = ref<(TemplateWithShop | GalleryTemplateItem)[]>([]);

    const updateFilteredList = () => {
      let items = list.value;

      // Apply search filter for gallery items
      if (templateCategorySelected.value.category === 'gallery' && searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        items = items.filter((record) => {
          if (isGalleryItem(record)) {
            const label = getLocalizedText(record.title, '').toLowerCase();
            const desc = getLocalizedText(record.description, '').toLowerCase();
            return label.includes(query) || desc.includes(query);
          }
          return true;
        });
      }

      // If not gallery or no authors selected, show filtered items
      if (templateCategorySelected.value.category !== 'gallery' || selectedAuthors.value.length === 0) {
        filteredList.value = items;
        return;
      }

      // Filter by selected authors. Selection values:
      //   - 'shopimind' → native ShopiMind items
      //   - 'me'        → templates created by the current shop user
      //   - <slug>      → items published by the integration with this slug
      filteredList.value = items.filter((record) => {
        if (isGalleryItem(record)) {
          // Integration-owned item — match by slug.
          if (record.author === 'integration' && record.integration_slug) {
            return selectedAuthors.value.includes(record.integration_slug);
          }
          // Legacy/native gallery items. Old format stores author as plain
          // string ('Shopimind' / something else) — treat anything not
          // marked as integration as ShopiMind.
          if (selectedAuthors.value.includes('shopimind')) {
            return true;
          }
          return false;
        }
        const isShopimind = record.id_shop === 0;
        if (selectedAuthors.value.includes('shopimind') && isShopimind) {
          return true;
        }
        if (selectedAuthors.value.includes('me') && !isShopimind) {
          return true;
        }
        return false;
      });
    };

    watch([list, selectedAuthors, templateCategorySelected], () => {
      updateFilteredList();
    }, { immediate: true, deep: true });

    const getWhatsappStatusLabel = (status: string): string => {
      const key = `templates.whatsappStatus.${status}`;
      const translated = t(key);
      return translated !== key ? translated : status;
    };

    const getWhatsappStatusIcon = (status: string): string => {
      const iconMap: Record<string, string> = {
        APPROVED: 'fa-solid fa-circle-check',
        REINSTATED: 'fa-solid fa-circle-check',
        PENDING: 'fa-solid fa-clock',
        IN_APPEAL: 'fa-solid fa-clock',
        REJECTED: 'fa-solid fa-circle-xmark',
        PAUSED: 'fa-solid fa-circle-pause',
        DISABLED: 'fa-solid fa-circle-minus',
        FLAGGED: 'fa-solid fa-flag',
        ARCHIVED: 'fa-solid fa-box-archive',
        DELETED: 'fa-solid fa-trash',
        PENDING_DELETION: 'fa-solid fa-trash',
        LIMIT_EXCEEDED: 'fa-solid fa-circle-exclamation',
        LOCKED: 'fa-solid fa-lock',
        ERROR: 'fa-solid fa-circle-exclamation',
      };
      return iconMap[status] || 'fa-solid fa-circle-question';
    };

    const getWhatsappStatusColors = (status: string): { bg: string; color: string } => {
      const sendable = ['APPROVED', 'REINSTATED'];
      const pending = ['PENDING', 'IN_APPEAL'];
      if (sendable.includes(status)) return { bg: '#ecfdf5', color: '#059669' };
      if (pending.includes(status)) return { bg: '#fffbeb', color: '#d97706' };
      return { bg: '#fef2f2', color: '#dc2626' };
    };

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const getListTemplates = async (loadMore: boolean) => {
      let {
        // eslint-disable-next-line prefer-const
        originCall,
      } = props;

      if (templatesType.value !== 'none') {
        if (loadMore) {
          isLoadingMore.value = true;
        } else {
          isListLoading.value = true;
        }

        const offset = loadMore ? list.value.length : 0;
        const result = await fetchUserTemplates(
          searchQuery.value,
          templateCategorySelected.value.category,
          props.customTemplateType || templatesType.value,
          originCall,
          limit,
          offset,
          selectedCampaign.value?.id,
        );

        if (result) {
          if (loadMore) {
            list.value = [...list.value, ...result.data as (TemplateWithShop | GalleryTemplateItem)[]];
          } else {
            list.value = result.data as (TemplateWithShop | GalleryTemplateItem)[];
          }
          totalCount.value = result.total;
          isListLoading.value = false;
          isLoadingMore.value = false;
          updateFilteredList();

          // Auto-switch to gallery if recent category is empty
          if (!loadMore && templateCategorySelected.value.category === 'recent' && list.value.length === 0) {
            const galleryOption = templateCategoriesOptions.value.find((opt) => opt.category === 'gallery');
            if (galleryOption) {
              templateCategorySelected.value = galleryOption;
            }
          }
        }
      }
    };

    // IntersectionObserver for infinite scroll
    const scrollSentinelRef = ref<HTMLElement | null>(null);
    let scrollObserver: IntersectionObserver | null = null;

    const setupScrollObserver = () => {
      if (scrollObserver) {
        scrollObserver.disconnect();
      }

      scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoadingMore.value && !isListLoading.value && list.value.length < totalCount.value) {
            getListTemplates(true);
          }
        },
        { threshold: 0.1 },
      );

      if (scrollSentinelRef.value) {
        scrollObserver.observe(scrollSentinelRef.value);
      }
    };

    watch(scrollSentinelRef, (el) => {
      if (el) {
        setupScrollObserver();
      }
    });

    const imageLoaded = (id_template: number) => {
      // Check if the image is already marked as loaded
      if (!imagesLoaded.value[id_template]) {
        imagesLoaded.value[id_template] = true;
        imageLoadTime.value[id_template] = new Date().getTime();
      }
    };

    const imageRetryCount = ref<Record<number, number>>({});

    const imageFailed = (id_template: number) => {
      // Initialize the counter if not already done
      if (!imageRetryCount.value[id_template]) {
        imageRetryCount.value[id_template] = 0;
      }

      // Check if we've already tried 3 times
      if (imageRetryCount.value[id_template] >= 3) {
        return;
      }

      // Increment the retry counter
      imageRetryCount.value[id_template] += 1;

      // If the image is not marked as loaded
      if (!imagesLoaded.value[id_template]) {
        setTimeout(() => {
          imageLoadTime.value[id_template] = new Date().getTime();
        }, 3000);
      }
    };

    // Helper function to get the first image key from a JSON array or return as is
    const getFirstImageKey = (imageKey: string | undefined | null): string => {
      if (!imageKey) return '';
      try {
        const parsed = JSON.parse(imageKey);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      } catch {
        // Not JSON, return as is
      }
      return imageKey;
    };

    watch([templateCategorySelected, searchQuery], async (newValues, oldValues) => {
      const [newCategory] = newValues;
      const currentCategory = newCategory.category;

      // Only reload if the category has really changed
      if (currentCategory !== previousCategory.value) {
        previousCategory.value = currentCategory;
        // Reset scenario filters when switching categories
        selectedCampaign.value = null;
        selectedAutomation.value = null;
        selectedBulk.value = null;
        automationOptions.value = [];
        bulkOptions.value = [];
        await getListTemplates(false);
      } else if (oldValues && newValues[1] !== oldValues[1]) {
        // For gallery, filter locally instead of re-fetching
        if (currentCategory === 'gallery') {
          updateFilteredList();
        } else {
          await getListTemplates(false);
        }
      }
    });

    // Re-fetch templates when scenario filter changes
    watch(selectedCampaign, async () => {
      if (templateCategorySelected.value.category === 'recent') {
        await getListTemplates(false);
      }
    });

    const handleChoose = async (idShopOfTemplate: number, idTemplate: number, label: string) => {
      store.commit('general/showTheSpinner');

      // If it's a gallery template, import via Nest API
      if (templateCategorySelected.value.category === 'gallery') {
        try {
          const idShopValue = UserState.activeShop?.id ?? 0;

          if (!idShopValue) {
            showToastError(t('templateSelector.errors.noShop'));
            store.commit('general/hideTheSpinner');
            return;
          }

          const selectedTemplate = list.value.find(
            (tmpl) => !isGalleryItem(tmpl) && tmpl.id_template === idTemplate,
          ) as TemplateWithShop | undefined;

          if (!selectedTemplate?.path) {
            showToastError(t('templateSelector.errors.noPath'));
            store.commit('general/hideTheSpinner');
            return;
          }

          // Build request body with optional parent references for redirect templates
          const createBody: Record<string, any> = {
            idShop: idShopValue,
            type: templatesType.value,
            baseId: selectedTemplate.id_template,
          };

          // Pass origin_template_id and name if this is a redirect (child template)
          const { baseLinkData } = props;
          if (baseLinkData && baseLinkData.originTemplateId) {
            createBody.originTemplateId = baseLinkData.originTemplateId;
            if (TemplateEditorState.template?.funnel?.template) {
              createBody.rootTemplateId = TemplateEditorState.template.funnel.template.id;
            }
            const generatedName = generateNextStepName(
              baseLinkData.namePrimaryTemplate || '',
              getTemplateParentTypeByType(templatesType.value),
              t,
              baseLinkData.selectedTemplate,
            );
            if (generatedName) {
              createBody.name = generatedName;
            }
          }
          const result = await nestCall(
            'post',
            NestApiType.DEFAULT,
            '/workflow/create-from-base',
            {},
            '',
            createBody,
          );

          if (result && result.success && result.id) {
            showToastSuccess(t('templateSelector.success.created'));
            context.emit('on-choose-template', asInt(result.id), '', templatesType.value);
            store.commit('general/hideTheSpinner');
          } else {
            showToastError(result?.message || t('templateSelector.errors.importFailed'));
            store.commit('general/hideTheSpinner');
          }
        } catch (error) {
          console.error('Template import error:', error);
          showToastError(t('templateSelector.errors.importFailed'));
          store.commit('general/hideTheSpinner');
        }
        return;
      }

      let name: string | null = null;
      // Duplicate template

      const mustDuplicate = props.forceDuplicate || (props.duplicate
      && !(props.originCall === 'templateBuilder' && !props.idCampaign
        && templateCategorySelected.value.category === 'self'
      ));

      if (mustDuplicate) {
        let {
          // eslint-disable-next-line prefer-const
          originCall, idCampaign, baseLinkData,
        } = props;

        if (originCall === 'myTemplates' || (originCall === 'templateBuilder' && !idCampaign)) {
          originCall = null;
          idCampaign = null;
          if (typeof baseLinkData === 'object'
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'namePrimaryTemplate')
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'countRedirectTemplate')
              && baseLinkData.namePrimaryTemplate
              && typeof baseLinkData.countRedirectTemplate === 'number') {
            name = `${baseLinkData.namePrimaryTemplate} - ${t(`templates.channels.${templatesType.value}`)} #${baseLinkData.countRedirectTemplate + 1}`;
          }
        } else {
          // Choose template from campaign
          // eslint-disable-next-line no-lonely-if
          if (typeof baseLinkData === 'object'
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'namePrimaryTemplate')
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'countRedirectTemplate')
              && baseLinkData.namePrimaryTemplate
              && typeof baseLinkData.countRedirectTemplate === 'number') {
            name = `${baseLinkData.namePrimaryTemplate} - ${t(`templates.channels.${templatesType.value}`)} #${baseLinkData.countRedirectTemplate + 1}`;
          } else if (props.currentTemplateName) {
            // Replace mode: preserve the current template name
            name = props.currentTemplateName;
          } else {
            // Add mode: generate incremental name
            const boxTypeToCount = mappingTemplateTypeToOperatorType[templatesType.value];
            const activeFlow = store.getters['flowEditor/getActiveFlow'];
            const countOperatorType = activeFlow?.flowData?.steps
              ? activeFlow.flowData.steps.filter((s: any) => s.type === boxTypeToCount).length
              : getCountOperatorsByType(boxTypeToCount);
            name = `${t(`templates.channels.${templatesType.value}`)} #${countOperatorType + 1}`;
          }
        }

        let originTemplateId = null;
        let rootTemplateId = null;
        if (baseLinkData) {
          originTemplateId = baseLinkData.originTemplateId || null;
        }
        if (originTemplateId && baseLinkData && baseLinkData.namePrimaryTemplate) {
          const { namePrimaryTemplate, selectedTemplate } = baseLinkData;
          name = generateNextStepName(namePrimaryTemplate, getTemplateParentTypeByType(templatesType.value), t, selectedTemplate);
          if (TemplateEditorState.template && TemplateEditorState.template.funnel && TemplateEditorState.template.funnel.template) {
            rootTemplateId = TemplateEditorState.template.funnel.template.id;
          }
        }

        const { duplicateTemplate } = useTemplatesApi();
        duplicateTemplate(idTemplate, {
          name,
          idShopOfTemplate,
          originCall,
          idCampaign,
          originTemplateId,
          rootTemplateId,
        }).then((result) => {
          if (result && result.success && result.data?.id_template) {
            // Small delay to ensure template is available in database before loading
            setTimeout(() => {
              context.emit('on-choose-template', asInt(result.data!.id_template), '', templatesType.value);
            }, 300);
          } else {
            showToastError(t('templateSelector.errors.duplicateFailed'));
            store.commit('general/hideTheSpinner');
          }
        }).catch((error) => {
          console.error('Template duplication error:', error);
          showToastError(t('templateSelector.errors.duplicateFailed'));
          store.commit('general/hideTheSpinner');
        });
      } else {
        context.emit('on-choose-template', idTemplate, label);
      }
    };

    // Handle gallery template selection — import via createFromBase with path as baseId
    const handleGalleryChoose = async (record: GalleryTemplateItem) => {
      // Pre-flight integration gating (same pattern as the campaign pickers).
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');
      try {
        const idShopValue = UserState.activeShop?.id ?? 0;
        if (!idShopValue) {
          showToastError(t('templateSelector.errors.noShop'));
          store.commit('general/hideTheSpinner');
          return;
        }

        if (!record.path) {
          showToastError(t('templateSelector.errors.noPath'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const createBody: Record<string, any> = {
          idShop: idShopValue,
          type: templatesType.value,
          baseId: record.path,
        };

        // Pass origin_template_id and name if this is a redirect (child template)
        const { baseLinkData } = props;
        if (baseLinkData && baseLinkData.originTemplateId) {
          createBody.originTemplateId = baseLinkData.originTemplateId;
          if (TemplateEditorState.template?.funnel?.template) {
            createBody.rootTemplateId = TemplateEditorState.template.funnel.template.id;
          }
          const generatedName = generateNextStepName(
            baseLinkData.namePrimaryTemplate || '',
            getTemplateParentTypeByType(templatesType.value),
            t,
            baseLinkData.selectedTemplate,
          );
          if (generatedName) {
            createBody.name = generatedName;
          }
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          createBody,
        );

        // Backend safety net (deactivation between page load and click).
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('templateSelector.success.created'));
          context.emit('on-choose-template', asInt(result.id), '', templatesType.value);
        } else {
          showToastError(result?.message || t('templateSelector.errors.importFailed'));
        }
      } catch (error) {
        console.error('Gallery template import error:', error);
        showToastError(t('templateSelector.errors.importFailed'));
      }
      store.commit('general/hideTheSpinner');
    };

    // ========================================================================
    // HTML Preview (uses shared TemplateHtmlPreviewSlider component)
    // ========================================================================
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const openHtmlPreview = async (idShopOfTemplate: number, idTemplate: number) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const idTemplateB64 = btoa(String(idTemplate));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop: idShopOfTemplate, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('templateSelector.htmlPreview.error'));
        }
      } catch {
        showToastError(t('templateSelector.htmlPreview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    const openGalleryHtmlPreview = async (record: GalleryTemplateItem) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('templateSelector.htmlPreview.error'));
        }
      } catch {
        showToastError(t('templateSelector.htmlPreview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const handleClosePreview = () => {
      displayPreviewModal.value = false;
    };

    const chooseTemplatesType = async (type: string) => {
      templatesType.value = type;
      await getListTemplates(false);
    };

    const handleDialogVisibilityChange = (visible: boolean) => {
      if (!visible) {
        context.emit('on-close-dialog');
      }
    };

    onMounted(async () => {
      // Open dialog after component is mounted to avoid initial @hide trigger
      await nextTick();
      displayDialog.value = true;
      await getListTemplates(false);
    });

    onBeforeUnmount(() => {
      if (scrollObserver) {
        scrollObserver.disconnect();
      }
    });

    const getTemplateTypeFlag = getFeatureFlagForTemplateType;

    // Collapse an integration's many gallery templates into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    return {
      t,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      idShop,
      baseUrl,
      getTemplateTypeFlag,
      displayPreviewModal,
      isListLoading,
      templateCategorySelected,
      templateCategoriesOptions,
      searchQuery,
      closeModal,
      handleChoose,
      handleClosePreview,
      imageLoadTime,
      imageLoaded,
      imageFailed,
      templatesType,
      displayDialog,
      listTypesConfig,
      chooseTemplatesType,
      handleDialogVisibilityChange,
      getFirstImageKey,
      // Author filter and filtered list
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      filteredList,
      getAuthorName,
      getWhatsappStatusLabel,
      getWhatsappStatusIcon,
      getWhatsappStatusColors,
      // Gallery template helpers
      isGalleryItem,
      getLocalizedText,
      handleGalleryChoose,
      // Require-integration modal
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      // HTML preview
      htmlPreviewData,
      openHtmlPreview,
      closeHtmlPreview,
      openGalleryHtmlPreview,
      // Infinite scroll
      totalCount,
      isLoadingMore,
      scrollSentinelRef,
      // Scenario filters
      selectedAutomation,
      automationOptions,
      automationLoading,
      selectedBulk,
      bulkOptions,
      bulkLoading,
      onAutomationShow,
      onBulkShow,
      onCampaignFilter,
    };
  },

  methods: { isDisplayTemplate },
});
</script>

<style scoped lang="scss">
// ==========================================
// Custom modal header
// ==========================================
.modal-header-custom {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.5rem 0;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #94c748;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.5rem;
    color: #ffffff;
  }
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.header-subtitle {
  font-size: 0.9375rem;
  color: #6c757d;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
}

// ==========================================
// Template type selection modal (grid layout)
// ==========================================
.template-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  padding: 0.5rem;
}

.template-type-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #94c748;
    background: #f8fff0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(148, 199, 72, 0.15);

    .template-type-card__icon {
      background: #94c748;
      border-color: #94c748;

      i {
        color: #ffffff;
      }
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    margin-bottom: 0.75rem;
    transition: all 0.2s ease;

    i {
      font-size: 1.25rem;
      color: #64748b;
      transition: color 0.2s ease;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #334155;
  }

  &__desc {
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.3;
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;

    &:hover {
      border-color: #e5e7eb;
      background: #ffffff;
      transform: none;
      box-shadow: none;
    }
  }
}

// ==========================================
// Template selector layout (sidebar left)
// ==========================================
.template-selector-layout {
  display: block;
  background: #f5f7fa;
  min-height: 100%;
  padding: 1.5rem;
  padding-left: calc(280px + 3rem);
  position: relative;
}

// Left sidebar - Filters (floating style with position fixed)
.filters-sidebar {
  width: 280px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: fit-content;
  position: fixed;
  top: calc(60px + 1.5rem);
  left: 1.5rem;
  z-index: 100;
  max-height: calc(100vh - 60px - 3rem);
  overflow-y: auto;

  &__content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &__label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    i {
      font-size: 1rem;
      color: #94c748;
    }
  }
}

// Search wrapper
.search-wrapper {
  position: relative;
  width: 100%;
}

// Filter options (vertical list)
.filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  color: #475569;
  background: #f8fafc;
  border: 1px solid transparent;

  i {
    font-size: 1rem;
    color: #94a3b8;
    width: 20px;
    text-align: center;
    transition: color 0.2s ease;
  }

  &:hover {
    background: #f1f5f9;
    border-color: #e2e8f0;
  }

  &--active {
    background: linear-gradient(135deg, #94c748 0%, #7db33a 100%);
    color: #ffffff;
    font-weight: 600;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(148, 199, 72, 0.3);

    i {
      color: #ffffff;
    }

    &:hover {
      background: linear-gradient(135deg, #82b33d 0%, #6fa030 100%);
    }
  }
}

// Results count
.filter-results {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-top: 0.5rem;

  &__count {
    font-size: 1.5rem;
    font-weight: 700;
    color: #94c748;
    line-height: 1;
  }

  &__label {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
  }
}

// ============ Author filter — visual card list ============
// Same UX as CampaignAutomationSelectorV2 + CampaignsBulkBaseSelectorV2.
// ShopiMind + "Me" sit at the top; partner integrations get their own
// subsection with inline search + scrollable list past 5 entries.
.author-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  &--scrollable {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 0.35rem;
    padding-right: 2px;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 9999px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #9ca3af;
    }
  }

  &__subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: 0.75rem 0.25rem 0.35rem;
    padding-top: 0.6rem;
    border-top: 1px solid #f3f4f6;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
  }

  &__subcount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #ede9fe;
    color: #6b21a8;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
  }

  &__search {
    position: relative;
    margin-bottom: 0.35rem;

    i {
      position: absolute;
      top: 50%;
      left: 0.55rem;
      transform: translateY(-50%);
      color: #9ca3af;
      font-size: 0.75rem;
      pointer-events: none;
    }

    input {
      width: 100%;
      padding: 0.4rem 0.5rem 0.4rem 1.8rem;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.8rem;
      background: #ffffff;
      transition: border-color 0.15s ease;

      &:focus {
        outline: none;
        border-color: #c4b5fd;
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
      }
    }
  }

  &__empty {
    padding: 0.8rem 0.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    font-style: italic;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border: 1.5px solid transparent;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }

    &--active {
      background: #f5f3ff;
      border-color: #c4b5fd;
    }

    &--integration:not(.author-list__item--active) {
      background: linear-gradient(90deg, #faf5ff 0%, #ffffff 12%);
    }

    &--integration.author-list__item--active {
      background: #ede9fe;
      border-color: #8b5cf6;
    }
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 18px;
      height: 18px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.85rem;
      color: #6b7280;
    }
  }

  &__item--integration .author-list__avatar {
    border-color: #c4b5fd;

    i {
      color: #7c3aed;
    }
  }

  &__item--active .author-list__avatar {
    border-color: #8b5cf6;
  }

  &__label {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    flex-shrink: 0;
    min-width: 20px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9999px;
    background: #ece9fb;
    color: #6d28d9;
    font-size: 0.7rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__item--active &__label {
    font-weight: 600;
    color: #1f2937;
  }

  &__check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #8b5cf6;
    color: #ffffff;
    font-size: 0.55rem;
    flex-shrink: 0;
  }

  &__item--active:not(.author-list__item--integration) &__check {
    background: #94c748;
  }
}

// Legacy MultiSelect styling (kept in case it's still imported elsewhere).
.author-multiselect {
  width: 100%;

  :deep(.p-multiselect) {
    width: 100%;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &.p-focus {
      border-color: #94c748;
      box-shadow: 0 0 0 3px rgba(148, 199, 72, 0.1);
    }

    .p-multiselect-label {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
    }
  }
}

// Scenario Dropdown
.scenario-dropdown {
  width: 100%;

  :deep(.p-dropdown) {
    width: 100%;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &.p-focus {
      border-color: #94c748;
      box-shadow: 0 0 0 3px rgba(148, 199, 72, 0.1);
    }

    .p-dropdown-label {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
    }

    .p-dropdown-filter {
      font-size: 0.8125rem;
    }
  }
}

.scenario-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;

  i {
    color: #6c757d;
    width: 1rem;
    text-align: center;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Search input
.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94c748;
  font-size: 1rem;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.75rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:hover {
    border-color: #cbd5e1;
    background: #ffffff;
  }

  &:focus {
    border-color: #94c748;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(148, 199, 72, 0.12);
  }
}

.clear-search-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0;
  border: none;
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  color: #64748b;

  &:hover {
    background: #94c748;
    color: #ffffff;
  }

  i {
    font-size: 0.5625rem;
  }
}

// Templates content
.templates-content {
  flex: 1;
  padding: 0;
  background: transparent;
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
  animation: fadeIn 0.4s ease-in-out;
}

// Template card
.template-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  /* `overflow: visible` so the partner ribbon can overhang the card.
     Same pattern as CampaignAutomationSelectorV2. */
  overflow: visible;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: #94c748;
  }

  /* Integration variant — violet tint + ring + lifted shadow on hover. */
  &--integration {
    background: linear-gradient(180deg, #faf5ff 0%, #ffffff 38%);
    border: 1.5px solid #c4b5fd;
    box-shadow: 0 1px 3px rgba(139, 92, 246, 0.08);

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 10px 28px rgba(139, 92, 246, 0.22);
    }
  }

  /* Partner ribbon — overhangs the top-left corner. z-index 20 stays
     above the hover overlay (z-index 10) so the partner branding
     remains visible while the user interacts with the card. */
  &__partner-ribbon {
    position: absolute;
    top: -12px;
    left: -10px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 5px 12px 5px 6px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    border-radius: 9999px;
    box-shadow: 0 6px 16px rgba(91, 33, 182, 0.22);
    pointer-events: none;
  }

  &__partner-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 16px;
      height: 16px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.75rem;
      color: #7c3aed;
    }
  }

  &__partner-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;
  }

  &__partner-kicker {
    font-size: 0.55rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #7c3aed;
  }

  &__partner-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__image {
    position: relative;
    width: 100%;
    height: 350px;
    background: #f8f8f8;
    /* Round the top corners — the parent card lost its overflow:hidden
       so the image must clip its own corners. */
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    overflow: hidden;
  }

  &__preview {
    width: 100%;
    height: 100%;
    background-size: 100% auto;
    background-position: center top;
    background-repeat: no-repeat;
    transition: background-position 3s ease-in-out;

    &--loading {
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
    }

    &--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);

      i {
        font-size: 2.5rem;
        color: #94a3b8;
      }
    }

    .template-card:hover & {
      background-position: center bottom;
    }
  }

  :deep(.template-card__html-preview-btn) {
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    z-index: 2;
    padding: 0;

    i {
      font-size: 0.875rem;
    }

    &:hover {
      background: #94c748;
      color: #ffffff;
      transform: scale(1.05);
    }
  }

  &:hover :deep(.template-card__html-preview-btn) {
    opacity: 1;
    transform: scale(1);
  }

  &__body {
    padding: 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__scenario {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #6c757d;
    font-style: italic;
    line-height: 1.2;
    overflow: hidden;

    i {
      flex-shrink: 0;
      font-size: 0.6875rem;
    }

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__description {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__author {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #64748b;

    i {
      font-size: 0.6875rem;
      color: #94a3b8;
    }
  }

  &__actions {
    padding: 0.75rem 1rem;
    border-top: 1px solid #f3f4f6;
    display: flex;
    gap: 0.5rem;
    background: #fafafa;

    :deep(.p-button) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;

      &.choose-btn {
        background: #94c748;
        border-color: #94c748;
        font-weight: 500;
        flex: 1;
        justify-content: center;
        color: white;

        &:hover {
          background: #82b33d;
          border-color: #82b33d;
        }
      }
    }
  }
}

// Load more spinner
.load-more-spinner {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #9ca3af;
  text-align: center;

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ==========================================
// Responsive
// ==========================================
@media (max-width: 900px) {
  .template-selector-layout {
    display: block;
    padding: 1.25rem;
    padding-left: 1.25rem;
  }

  .filters-sidebar {
    width: 100%;
    position: relative;
    top: 0;
    left: 0;
    max-height: none;
    margin-bottom: 1.25rem;

    &__content {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1.25rem;
      padding: 1.25rem;
    }
  }

  .filter-section {
    flex: 1;
    min-width: 200px;
  }

  .filter-options {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .filter-option {
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
  }

  .filter-results {
    margin-top: 0;
    flex: 0 0 auto;
  }

  .templates-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.25rem;
  }
}

@media (max-width: 600px) {
  .filters-sidebar__content {
    flex-direction: column;
  }

  .filter-section {
    min-width: 100%;
  }

  .templates-content {
    padding: 1rem;
  }

  .templates-grid {
    grid-template-columns: 1fr;
  }

  .template-card__image {
    height: 350px;
  }

  .template-card__actions {
    flex-direction: column;

    :deep(.p-button) {
      width: 100%;

      &.preview-btn {
        order: 2;
      }

      &.choose-btn {
        order: 1;
      }
    }
  }

  .template-type-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<style lang="scss">
// Override EditorModal styles when template selector is open
.content.bg-white:has(.template-selector-layout) {
  background: #f5f7fa !important;
}
</style>
